import { forwardRef, InputHTMLAttributes, useState } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  iconLeft?: string;
  iconRight?: string;
  onIconRightClick?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      required,
      iconLeft,
      iconRight,
      onIconRightClick,
      className = '',
      id,
      type,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;
    const inputId = id ?? `input-${Math.random().toString(36).slice(2)}`;

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground-700 font-label"
          >
            {label}
            {required && (
              <span className="ml-1 text-semantic-error" aria-hidden="true">*</span>
            )}
          </label>
        )}

        <div className="relative flex items-center">
          {iconLeft && (
            <span className="absolute left-3 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
              <i className={`${iconLeft} text-base`} />
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            type={inputType}
            required={required}
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            className={`
              xl-input
              ${iconLeft ? 'pl-10' : ''}
              ${iconRight || isPassword ? 'pr-10' : ''}
              ${error ? 'error' : ''}
              ${className}
            `}
            {...rest}
          />

          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
            >
              <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
            </button>
          )}

          {!isPassword && iconRight && (
            <button
              type="button"
              tabIndex={-1}
              onClick={onIconRightClick}
              className="absolute right-3 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
            >
              <i className={`${iconRight} text-base`} />
            </button>
          )}
        </div>

        {error && (
          <p id={`${inputId}-error`} role="alert" className="flex items-center gap-1 text-xs text-semantic-error font-label">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="text-xs text-foreground-500 font-label">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;